<template>
  <DrawerModal
    :is-open="isOpen"
    title="profile.my.email.title"
    :has-changes="isFormChanged"
    :is-loading="isLoading"
    :is-disabled="!isFormChanged || isLoading"
    @close="handleClose"
    @submit="saveEmailPreferences"
  >
    <div class="profile-email-preferences">
      <div class="section-card">
        <GamSwitch v-bind="emailIrlConfirmedSwitch()" v-model:checked="emailPreferences.sparks" />
      </div>

      <div class="section-card">
        <GamSwitch v-bind="emailDailyNewsSwitch()" v-model:checked="emailPreferences.daily" />
      </div>
    </div>
  </DrawerModal>
</template>

<script setup lang="ts">
import {
  EmailCategory,
  getDefaultEmailPreferences,
  getDefaultPushPreferences,
  type NotificationPreferences,
  SmsCategory,
} from '@/core/data/user/user.interface';
import { localize } from '@/core/gambits';
import { useAuthStore } from '@/stores/auth.store';
import DrawerModal from '@/views/components/DrawerModal.vue';
import GamSwitch from '@/views/components/GamSwitch.vue';
import { GamSwitchPosition, GamSwitchValueType } from '@/views/composables/constants/components/gamSwitch.constants';
import type { GamSwitchType } from '@/views/composables/models/components/GamSwitch';
import { watchDeep } from '@vueuse/core';
import { storeToRefs } from 'pinia';
import { nextTick, reactive, ref, watch } from 'vue';
import { useToast } from 'vue-toastification';

interface EmailPreferencesProps {
  isOpen: boolean;
}

defineProps<EmailPreferencesProps>();
const emit = defineEmits<{ close: [] }>();

const handleClose = () => {
  hydrateFromUserDetails();
  emit('close');
};

const authStore = useAuthStore();
const { userDetails } = storeToRefs(authStore);

const isLoading = ref<boolean>(false);
const isFormChanged = ref<boolean>(false);
const isHydrating = ref(false);

const readEmailRowFromStore = () => {
  const e = userDetails.value?.notificationPreferences?.email;

  return { ...getDefaultEmailPreferences(), ...e };
};

const emailPreferences = reactive(readEmailRowFromStore());

const hydrateFromUserDetails = () => {
  isHydrating.value = true;
  try {
    Object.assign(emailPreferences, readEmailRowFromStore());
    isFormChanged.value = false;
  } finally {
    void nextTick(() => {
      isHydrating.value = false;
    });
  }
};

watch(
  () => userDetails.value?.notificationPreferences,
  () => {
    hydrateFromUserDetails();
  },
  { deep: true },
);

watchDeep(emailPreferences, () => {
  if (isHydrating.value) return;
  isFormChanged.value = true;
});

const saveEmailPreferences = async () => {
  isLoading.value = true;

  const currentPrefs = userDetails.value?.notificationPreferences;
  const currentEmail = currentPrefs?.email ?? getDefaultEmailPreferences();
  const notificationPreferences: NotificationPreferences = {
    email: {
      ...currentEmail,
      [EmailCategory.SPARKS]: emailPreferences.sparks,
      [EmailCategory.DAILY]: emailPreferences.daily,
    },
    sms: currentPrefs?.sms ?? { [SmsCategory.SPARKS]: false },
    ...(currentPrefs?.push !== undefined ? { push: { ...getDefaultPushPreferences(), ...currentPrefs.push } } : {}),
  };

  try {
    const saved = await authStore.updateNotificationPreferences(notificationPreferences);
    if (!saved) {
      useToast().error(localize('profile.my.notification.save_error'));
      isLoading.value = false;

      return;
    }

    isFormChanged.value = false;
    emit('close');
  } catch (e) {
    useToast().error(localize('profile.my.notification.save_error'));
    // eslint-disable-next-line no-console
    console.error('Failed to save email preferences', e);
  }
  isLoading.value = false;
};

const emailIrlConfirmedSwitch = (): GamSwitchType => ({
  label: 'profile.my.email.irl_confirmed',
  name: 'profile.my.email.irl_confirmed',
  type: GamSwitchValueType.SWITCH,
  position: GamSwitchPosition.RIGHT,
});

const emailDailyNewsSwitch = (): GamSwitchType => ({
  label: 'profile.my.email.daily_news',
  name: 'profile.my.email.daily_news',
  type: GamSwitchValueType.SWITCH,
  position: GamSwitchPosition.RIGHT,
});
</script>

<style scoped lang="scss">
.profile-email-preferences {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-l);

  .section-card {
    display: flex;
    flex-direction: column;
    padding: var(--spacing-l);
    border-radius: var(--radius-small);
    border: 1px solid var(--color-white-10);

    :deep(.gam-switch-content .label-text) {
      font-weight: 700;
    }
  }
}
</style>
